import {
  createContext,
  useContext,
  useState,
  useMemo,
  PropsWithChildren,
  memo,
} from 'react';

export type TDictationKey = string | null;

export type DictationKeyState = {
  activeKey: TDictationKey;
  setActiveKey: React.Dispatch<React.SetStateAction<TDictationKey>>;
};

const CosmosDictationKeyContext = createContext<DictationKeyState | undefined>(undefined);

export const useCosmosDictationKey = () => {
  const context = useContext(CosmosDictationKeyContext);
  if (!context) {
    throw new Error(
      'useCosmosDictationKey must be used within a CosmosDictationKeyProvider',
    );
  }
  return context;
};

/**
 * Context to manage the active mic key across the app
 * Main usage is specifically for "useDictation" hook.
 */

export const CosmosDictationKeyProvider = memo(({ children }: PropsWithChildren) => {
  const [activeKey, setActiveKey] = useState<TDictationKey>(null);

  const contextValue = useMemo(() => {
    return {
      activeKey: activeKey,
      setActiveKey: setActiveKey,
    };
  }, [activeKey]);

  return (
    <CosmosDictationKeyContext.Provider value={contextValue}>
      {children}
    </CosmosDictationKeyContext.Provider>
  );
});
CosmosDictationKeyProvider.displayName = 'CosmosDictationKeyProvider';
